import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Spin } from 'antd';
import PropTypes from 'prop-types';
import {
  fetchChallengersSummary,
  promoteChallenger,
  revertCurrentChampion
} from '../../../../redux/actions/trm/trmDashboard';
import {
  getChampionColumns,
  getPreviousChallengersColumns,
  getPreviousChampionsColumns
} from './columns';
import TypologyRules from '../../components/typologies/TypologyRules';
import { Container, StyledTable } from '../styled';
import TypologyCarousel from '../../components/typologies/carousel/TypologyCarousel';
import useAlertLevelConfigs from '../../../common/hooks/useAlertLevelConfig';

const Summary = ({
  dispatch,
  authenticationData,
  sourceId,
  isScheduled,
  isFraudPrevention,
  summaryData,
  isLoading,
  alertLevels = []
}) => {
  const { champion, challengers = [], previousChampions = [] } = summaryData;
  const { getLevelColor } = useAlertLevelConfigs(alertLevels);

  useEffect(() => {
    if (sourceId) {
      dispatch(fetchChallengersSummary({ sourceId, scheduled: isScheduled }));
    }
  }, [sourceId, isScheduled]);

  const onRevertCurrentChampion = useCallback(
    (currentChampionId) => {
      dispatch(
        revertCurrentChampion({
          currentChampionId,
          sourceId, // pass the sourceId to revert saga for call the summay api after success.
          scheduled: isScheduled
        })
      );
    },
    [sourceId, isScheduled]
  );

  const onPromoteChallenger = useCallback(
    (challengerId) => {
      //pass the sourceId and scheduled to promote saga for call the summary api after success.
      dispatch(
        promoteChallenger({
          challengerId,
          sourceId,
          scheduled: isScheduled
        })
      );
    },
    [sourceId, isScheduled]
  );

  const championColumns = useMemo(() => {
    return getChampionColumns(isFraudPrevention, getLevelColor);
  }, [isFraudPrevention, getLevelColor]);

  const previousChampionColumns = useMemo(() => {
    return getPreviousChampionsColumns(
      authenticationData,
      isFraudPrevention,
      onRevertCurrentChampion,
      getLevelColor
    );
  }, [
    authenticationData,
    isFraudPrevention,
    onRevertCurrentChampion,
    getLevelColor
  ]);

  const previousChallengerColumns = useMemo(() => {
    return getPreviousChallengersColumns(
      authenticationData,
      isFraudPrevention,
      onPromoteChallenger,
      getLevelColor
    );
  }, [
    authenticationData,
    isFraudPrevention,
    onPromoteChallenger,
    getLevelColor
  ]);

  const filteredPreviousChampions = useMemo(() => {
    return previousChampions.filter(({ state }) => state === 'Promoted');
  }, [previousChampions]);
  const filteredChallengers = useMemo(() => {
    return challengers.filter(({ state }) => state === 'Created');
  }, [challengers]);

  return (
    <Spin spinning={isLoading}>
      <Container>
        <Label>Current Champion</Label>
        <StyledTable
          tableLayout={'auto'}
          columns={championColumns}
          dataSource={champion ? [champion] : []}
          pagination={false}
        />
        {isFraudPrevention ? (
          <TypologyRules typologies={champion?.typologies} />
        ) : (
          <SetsContainer>
            <TypologyCarousel
              headerLabel={'Current Champion'}
              typologySets={champion?.categorizedTypologies || []}
            />
          </SetsContainer>
        )}
      </Container>
      <Container>
        <Label>Previous Champions</Label>
        {filteredPreviousChampions.length === 0 && (
          <StyledTable
            tableLayout={'auto'}
            columns={previousChampionColumns}
            dataSource={[]}
            pagination={false}
          />
        )}
        {filteredPreviousChampions.length > 0 &&
          filteredPreviousChampions.map((previousChampion) => (
            <ChampionSingleTable key={previousChampion.challengerId}>
              <StyledTable
                tableLayout={'auto'}
                columns={previousChampionColumns}
                dataSource={[previousChampion]}
                pagination={false}
              />
              {isFraudPrevention ? (
                <TypologyRules
                  typologies={previousChampion?.typologies || []}
                />
              ) : (
                <SetsContainer>
                  <TypologyCarousel
                    headerLabel={'Previous Champions'}
                    typologySets={previousChampion?.categorizedTypologies || []}
                  />
                </SetsContainer>
              )}
            </ChampionSingleTable>
          ))}
      </Container>
      <Container>
        <Label>Challengers</Label>
        {filteredChallengers.length === 0 && (
          <StyledTable
            tableLayout={'auto'}
            columns={previousChallengerColumns}
            dataSource={[]}
            pagination={false}
          />
        )}
        {filteredChallengers.length > 0 &&
          filteredChallengers.map((challenger) => (
            <ChampionSingleTable key={challenger.challengerId}>
              <StyledTable
                tableLayout={'auto'}
                columns={previousChallengerColumns}
                dataSource={[challenger]}
                pagination={false}
              />
              {isFraudPrevention ? (
                <TypologyRules typologies={challenger?.typologies || []} />
              ) : (
                <SetsContainer>
                  <TypologyCarousel
                    headerLabel={`Challenger ID : ${challenger.challengerId}`}
                    typologySets={challenger?.categorizedTypologies || []}
                  />
                </SetsContainer>
              )}
            </ChampionSingleTable>
          ))}
      </Container>
    </Spin>
  );
};

const Label = styled.div`
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 4px;
`;

const ChampionSingleTable = styled.div`
  margin-bottom: 24px;

  &:last-of-child {
    margin-bottom: 0;
  }
`;

const SetsContainer = styled.div`
  font-size: 12px;
  margin-top: 8px;
  border-bottom: 1px solid #d9d9d9;
`;

Summary.propTypes = {
  dispatch: PropTypes.func.isRequired,
  authenticationData: PropTypes.array,
  sourceId: PropTypes.string,
  isScheduled: PropTypes.bool,
  isFraudPrevention: PropTypes.bool,
  summaryData: PropTypes.object,
  isLoading: PropTypes.bool
};

const mapStateToProps = ({
  authentication,
  trmDashboard: { challengersSummary },
  amlsCommon
}) => ({
  authenticationData: authentication.data,
  summaryData: challengersSummary?.data || {},
  isLoading: challengersSummary?.isLoading || false,
  // FIN-12133: alert_level_details configs to colour-code the Action by severity.
  alertLevels: amlsCommon?.alertLevelConfigs?.data || []
});

export default connect(mapStateToProps)(Summary);
